import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";

type RefContact = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
};

export function ReferrerContactPanel({
  referrer,
  title = "Referrer & Details",
  children,
  defaultExpanded = false,
  expanded,
  onChange,
  leadStatus,
}: {
  referrer: RefContact;
  /** Panel title; defaults to "Referrer & Details" */
  title?: string;
  /** Optional extra content rendered below contact rows (e.g., chat button, tags) */
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
  leadStatus?: string;
}) {
  const name = referrer?.name || "—";
  const email = (leadStatus !== "NEW" && referrer?.email) || "";
  const phone = (leadStatus !== "NEW" && referrer?.phone) || "";

  const copy = async (v?: string | null) => {
    if (!v) return;
    try {
      await navigator.clipboard.writeText(v);
    } catch {}
  };

  const Row = ({
    label,
    value,
    actions,
    mono = false,
  }: {
    label: string;
    value?: string | null;
    actions?: React.ReactNode;
    mono?: boolean;
  }) => {
    if (!value) return null;
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          py: 0.75,
          borderBottom: "1px dashed",
          borderColor: "divider",
          "&:last-of-type": { borderBottom: "none" },
        }}
      >
        <Typography
          variant="body2"
          sx={{ minWidth: 88, color: "text.secondary" }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            wordBreak: "break-word",
            fontFamily: mono
              ? "ui-monospace, SFMono-Regular, Menlo, monospace"
              : undefined,
          }}
        >
          {value}
        </Typography>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ ml: "auto" }}
          alignItems="center"
        >
          {actions}
        </Stack>
      </Stack>
    );
  };

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onChange={(_event, nextExpanded) => onChange?.(nextExpanded)}
      disableGutters
      sx={{ bgcolor: "background.paper", borderRadius: 1 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.5,
          "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 },
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0.5 }}>
        {/* Name (static line) */}
        <Stack direction="row" alignItems="center" gap={1} sx={{ pb: 0.75 }}>
          <Typography
            variant="body2"
            sx={{ minWidth: 88, color: "text.secondary" }}
          >
            Name
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
        </Stack>

        {/* Email */}
        {email && (
          <Row
            label="Email"
            value={email}
            mono
            actions={
              <>
                <Tooltip title="Email">
                  <IconButton
                    size="small"
                    component="a"
                    href={`mailto:${email}`}
                  >
                    <EmailOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy email">
                  <IconButton size="small" onClick={() => copy(email)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </>
            }
          />
        )}

        {/* Phone */}
        {phone && (
          <Row
            label="Phone"
            value={phone}
            mono
            actions={
              <>
                <Tooltip title="Call">
                  <IconButton size="small" component="a" href={`tel:${phone}`}>
                    <PhoneOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy phone">
                  <IconButton size="small" onClick={() => copy(phone)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </>
            }
          />
        )}

        {!email && !phone && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No contact info on file for the referrer.
          </Alert>
        )}

        {children ? <Box sx={{ mt: 1.25 }}>{children}</Box> : null}
      </AccordionDetails>
    </Accordion>
  );
}
